import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Dialog,
  IconButton,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import GavelIcon from "@mui/icons-material/Gavel";
import BusinessIcon from "@mui/icons-material/Business";
import PublicIcon from "@mui/icons-material/Public";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { getSectionDetail } from "../../services/getSectionDetail";
import { Loader } from "../common/Loader/Loader";

const theme = createTheme({
  breakpoints: {
    values: {
      mobile: 0,
      tablet: 768,
      laptop: 1024,
      desktop: 1200,
    },
  },
});

const sectionTitleSx = {
  fontSize: { mobile: "28px", tablet: "36px" },
  fontWeight: 700,
  textAlign: "center",
  color: "#333333",
  lineHeight: "133.333%",
  fontFamily: "KaiseiDecol-Regular",
};

const subSectionTitleSx = {
  ...sectionTitleSx,
  fontSize: { mobile: "16px", tablet: "20px" },
};

const detailTextSx = {
  fontSize: "14px",
  fontWeight: 400,
  color: "#666666",
  fontFamily: "mulish-regular",
  lineHeight: "160%",
};

const isRemoteUrl = (value) => /^https?:\/\//i.test(value);

/* -------------------- Icon helpers -------------------- */

const getPolicyIcon = (title = "") => {
  const t = title.toLowerCase();
  if (t.includes("privacy")) return PrivacyTipIcon;
  if (t.includes("term") || t.includes("condition")) return GavelIcon;
  if (t.includes("certificate") || t.includes("cert")) return WorkspacePremiumIcon;
  return WorkspacePremiumIcon;
};

const getAffiliationIcon = (title = "") => {
  const t = title.toLowerCase();
  if (t.includes("google")) return PublicIcon;
  if (t.includes("facebook")) return FacebookIcon;
  if (t.includes("instagram")) return InstagramIcon;
  if (t.includes("twitter") || t === "x") return TwitterIcon;
  if (t.includes("linkedin")) return LinkedInIcon;
  if (t.includes("certificate") || t.includes("cert")) return WorkspacePremiumIcon;
  return BusinessIcon;
};

/* -------------------- Date helpers -------------------- */

const getOrdinalSuffix = (day) => {
  const v = day % 100;
  if (v >= 11 && v <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const formatEventDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const formatEventDateParts = (dateString) => {
  if (!dateString) return null;
  const date = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  const weekday = date.toLocaleDateString("en-US", { weekday: "short" });
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "long" });
  return {
    line1: `${weekday} ${day}${getOrdinalSuffix(day)}`,
    line2: month,
  };
};

/* -------------------- Preview Modal -------------------- */

const PreviewModal = ({ open, onClose, file, fileType, title }) => {
  const isPdf = fileType?.toLowerCase() === "pdf";
  const previewSrc = isPdf && isRemoteUrl(file)
    ? `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(file)}`
    : file;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#FFFFFF",
          borderRadius: "8px",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { mobile: "12px", laptop: "20px" },
          py: "12px",
          borderBottom: "1px solid #EEEEEE",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { mobile: "16px", laptop: "18px" },
            color: "#333333",
            fontFamily: "KaiseiDecol-Regular",
            pr: "12px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
          {file && (
            <Button
              component="a"
              href={file}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: "16px !important" }} />}
              sx={{
                color: "#333333",
                fontSize: "13px",
                textTransform: "capitalize",
                fontFamily: "mulish-regular",
                whiteSpace: "nowrap",
              }}
            >
              Open in new tab
            </Button>
          )}
          <IconButton onClick={onClose} size="small" aria-label="Close preview">
            <CloseIcon sx={{ color: "#333333" }} />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: "#F8F8F8",
          minHeight: { mobile: "40vh", laptop: "75vh" },
          maxHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
          p: { mobile: "12px", laptop: "16px" },
        }}
      >
        {previewSrc && isPdf && (
          <Box
            component="iframe"
            src={previewSrc}
            title={title}
            sx={{ width: "100%", minHeight: { mobile: "60vh", laptop: "75vh" }, border: "none" }}
          />
        )}

        {previewSrc && !isPdf && (
          <Box
            component="img"
            src={previewSrc}
            alt={title}
            sx={{
              display: "block",
              width: "auto",
              height: "auto",
              maxWidth: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
            }}
          />
        )}
      </Box>
    </Dialog>
  );
};

/* -------------------- Pill Carousel (Policies / Affiliations) -------------------- */

const PillCard = ({ icon: IconComp, title, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "14px",
      bgcolor: "#F5F5F5",
      borderRadius: "14px",
      p: "10px 24px 10px 10px",
      minWidth: { mobile: "220px", laptop: "260px" },
      flexShrink: 0,
      cursor: onClick ? "pointer" : "default",
      transition: "box-shadow 0.2s, background-color 0.2s",
      "&:hover": onClick
        ? { boxShadow: "0px 6px 16px rgba(51,51,51,0.12)", bgcolor: "#F0F0F0" }
        : {},
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "10px",
        bgcolor: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <IconComp sx={{ color: "#333333", fontSize: 22 }} />
    </Box>
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: "15px",
        fontFamily: "KaiseiDecol-Regular",
        color: "#333333",
        whiteSpace: "nowrap",
      }}
    >
      {title}
    </Typography>
  </Box>
);

const PillCarousel = ({ items, rows = 1 }) => {
  const rowRefs = useRef([]);

  const rowsData = useMemo(() => {
    if (!items?.length) return [];
    if (rows <= 1) return [items];
    const buckets = Array.from({ length: rows }, () => []);
    items.forEach((item, index) => buckets[index % rows].push(item));
    return buckets.filter((bucket) => bucket.length > 0);
  }, [items, rows]);

  const scrollBy = (direction) => {
    rowRefs.current.forEach((el) => {
      if (el) el.scrollBy({ left: direction * 300, behavior: "smooth" });
    });
  };

  if (!rowsData.length) return null;

  return (
    <Box sx={{ position: "relative", mt: { mobile: "16px", laptop: "24px" } }}>
      <IconButton
        onClick={() => scrollBy(-1)}
        aria-label="Scroll left"
        sx={{
          position: "absolute",
          left: { mobile: "-6px", laptop: "-20px" },
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
          bgcolor: "#FFFFFF",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.12)",
          width: 36,
          height: 36,
          "&:hover": { bgcolor: "#FFFFFF" },
        }}
      >
        <ArrowBackIosNewIcon sx={{ fontSize: 14, color: "#333333" }} />
      </IconButton>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          overflow: "hidden",
          mx: { mobile: "26px", laptop: "36px" },
        }}
      >
        {rowsData.map((rowItems, rIdx) => (
          <Box
            key={rIdx}
            ref={(el) => (rowRefs.current[rIdx] = el)}
            sx={{
              display: "flex",
              gap: "16px",
              mx: { mobile: "20px" },
              overflowX: "auto",
              scrollBehavior: "smooth",
              pb: "4px",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {rowItems.map((item, idx) => (
              <PillCard
                key={`${rIdx}-${idx}`}
                icon={item.icon}
                title={item.title}
                onClick={item.onClick}
              />
            ))}
          </Box>
        ))}
      </Box>

      <IconButton
        onClick={() => scrollBy(1)}
        aria-label="Scroll right"
        sx={{
          position: "absolute",
          right: { mobile: "-6px", laptop: "-20px" },
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 2,
          bgcolor: "#FFFFFF",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.12)",
          width: 36,
          height: 36,
          "&:hover": { bgcolor: "#FFFFFF" },
        }}
      >
        <ArrowForwardIosIcon sx={{ fontSize: 14, color: "#333333" }} />
      </IconButton>
    </Box>
  );
};

/* -------------------- Upcoming Events (alternating rows) -------------------- */

const EventRow = ({ event, reversed }) => {
  const dateParts = formatEventDateParts(event.date);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: reversed ? "row-reverse" : "row",
        alignItems: "center",
        gap: { mobile: "16px", laptop: "28px" },
        p: { mobile: "16px", laptop: "20px 28px" },
        borderBottom: "1px solid #EEEEEE",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      {dateParts && (
        <Box sx={{ minWidth: { mobile: "96px", laptop: "150px" }, flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: { mobile: "20px", laptop: "28px" },
              fontWeight: 800,
              color: "#333333",
              fontFamily: "KaiseiDecol-Regular",
              lineHeight: 1.15,
            }}
          >
            {dateParts.line1}
          </Typography>
          <Typography
            sx={{
              fontSize: { mobile: "20px", laptop: "28px" },
              fontWeight: 800,
              color: "#333333",
              fontFamily: "KaiseiDecol-Regular",
              lineHeight: 1.15,
            }}
          >
            {dateParts.line2}
          </Typography>
        </Box>
      )}

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { mobile: "16px", laptop: "18px" },
            color: "#333333",
            fontFamily: "KaiseiDecol-Regular",
            mb: "6px",
          }}
        >
          Event: {event.event_name}
        </Typography>

        {event.booth_number && (
          <Typography sx={detailTextSx}>Booth no : {event.booth_number}</Typography>
        )}
        {event.location && (
          <Typography sx={detailTextSx}>Location: {event.location}</Typography>
        )}
        {event.date && (
          <Typography sx={detailTextSx}>Date: {formatEventDate(event.date)}</Typography>
        )}
      </Box>

      {event.image && (
        <Box
          component="img"
          src={event.image}
          alt={event.event_name}
          sx={{
            width: { mobile: "84px", laptop: "112px" },
            height: { mobile: "84px", laptop: "112px" },
            objectFit: "cover",
            borderRadius: "8px",
            bgcolor: "#F8F8F8",
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  );
};

const EventsList = ({ events }) => (
  <Box
    sx={{
      mt: { mobile: "16px", laptop: "24px" },
      border: "1px solid #EEEEEE",
      borderRadius: "8px",
      overflow: "hidden",
      bgcolor: "#FFFFFF",
    }}
  >
    {events.map((event, index) => (
      <EventRow key={index} event={event} reversed={index % 2 === 1} />
    ))}
  </Box>
);

/* -------------------- Main Component -------------------- */

export const Legal = () => {
  const [legalSection, setLegalSection] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLegalSection = async () => {
      try {
        setIsLoading(true);
        const response = await getSectionDetail(window.config.vendor_id, "legal");
        const legalSectionData =
          response?.data?.response?.user_website_details?.legal_section ?? null;
        const hasLegalContent = Array.isArray(legalSectionData)
          ? legalSectionData.length > 0
          : Boolean(legalSectionData) &&
            Object.keys(legalSectionData || {}).length > 0;

        setLegalSection(legalSectionData);

        if (!hasLegalContent) {
          navigate("/");
        }
      } catch (error) {
        setLegalSection(null);
        navigate("/");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLegalSection();
  }, [navigate]);

  const handlePreview = (data) => setPreviewData(data);
  const handleClosePreview = () => setPreviewData(null);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: "300px",
        }}
      >
        <Loader />
      </Box>
    );
  }

  const policies = legalSection?.policies ?? [];
  const affiliations = legalSection?.affiliations ?? [];
  const upcomingEvents = legalSection?.upcoming_events ?? [];

  const hasPolicies = policies.length > 0;
  const hasAffiliations = affiliations.length > 0;
  const hasEvents = upcomingEvents.length > 0;
  const hasAnyData = hasPolicies || hasAffiliations || hasEvents;

  const policyItems = policies.map((policy) => ({
    title: policy.title,
    icon: getPolicyIcon(policy.title),
    onClick: policy.file
      ? () =>
          handlePreview({
            file: policy.file,
            fileType: policy.file_type,
            title: policy.title,
          })
      : undefined,
  }));

  const affiliationItems = affiliations.map((affiliation) => ({
    title: affiliation.title,
    icon: getAffiliationIcon(affiliation.title),
    onClick: affiliation.file
      ? () =>
          handlePreview({
            file: affiliation.file,
            fileType: affiliation.file_type,
            title: affiliation.title,
          })
      : undefined,
  }));

  return (
    <ThemeProvider theme={theme}>
      <Container
        sx={{
          "&.MuiContainer-root": {
            p: 0,
            maxWidth: "1215px",
          },
          pb: { mobile: "32px", laptop: "48px" },
        }}
      >
        <Box
          sx={{
            mt: { mobile: "24px", laptop: "48px" },
            mx: { mobile: "12px", laptop: "0px" },
          }}
        >
          {/* <Typography sx={sectionTitleSx}>
            Legal Documents & Policies
          </Typography> */}

          {!hasAnyData && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "240px",
                mt: { mobile: "24px", laptop: "48px" },
                bgcolor: "#F8F8F8",
                borderRadius: "4px",
              }}
            >
              <Typography
                sx={{
                  fontSize: { mobile: "16px", laptop: "18px" },
                  fontWeight: 500,
                  color: "#666666",
                  fontFamily: "mulish-regular",
                  textAlign: "center",
                  px: "16px",
                }}
              >
                No data available at the moment.
              </Typography>
            </Box>
          )}

          {hasPolicies && (
            <Box sx={{ mt: { mobile: "32px", laptop: "48px" } }}>
              <Typography sx={subSectionTitleSx}>Policies</Typography>
              <PillCarousel items={policyItems} rows={1} />
            </Box>
          )}

          {hasAffiliations && (
            <Box sx={{ mt: { mobile: "32px", laptop: "48px" } }}>
              <Typography sx={subSectionTitleSx}>Affiliations</Typography>
              <PillCarousel items={affiliationItems} rows={2} />
            </Box>
          )}

          {hasEvents && (
            <Box sx={{ mt: { mobile: "32px", laptop: "48px" } }}>
              <Typography sx={subSectionTitleSx}>Upcoming Events</Typography>
              <EventsList events={upcomingEvents} />
            </Box>
          )}
        </Box>
      </Container>

      <PreviewModal
        open={Boolean(previewData)}
        onClose={handleClosePreview}
        file={previewData?.file}
        fileType={previewData?.fileType}
        title={previewData?.title}
      />
    </ThemeProvider>
  );
};